import {
  Text,
  Stack,
  Tabs,
  Textarea,
  Box,
  LoadingOverlay,
  Flex,
  Loader,
  Group,
} from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import React from "react"
import { RuleGroupType } from "react-querybuilder"
import { useSearchParams } from "react-router-dom"

import { Position } from "@costory/types/endpoints/digest"
import { DatePreset } from "@costory/types/prisma-client"

import { CustomMarkdown } from "@costory/front/components/MarkdownEditor"
import { ContributionAnalysisWand } from "@costory/front/pages/DigestV2/components/SuggestedCards/ContributingFactors"

export const ContentEditor = ({
  textareaRef,
  loading,
  editContent,
  setEditContent,
  position,
  digestPeriod,
  exclusionWhereClause,
  costColumn,
  setSummaryValueFromBilly,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement>
  close: () => void // add close prop
  loading: boolean
  editContent: string
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  position: Position
  digestPeriod: string
  exclusionWhereClause: RuleGroupType
  costColumn: string
  setSummaryValueFromBilly: (summary: string) => void
}) => {
  const emptyPreview = (
    <Flex direction="column" align="center" justify="center" gap={8}>
      <IconSearch size={32} color="#adb5bd" />
      <Text size="sm" ta="center">
        Nothing to preview yet.
      </Text>
    </Flex>
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const tabValue = searchParams.get("mode") === "preview" ? "preview" : "edit"

  const emptyContent = editContent.trim() === ""
  return (
    <Box style={{ position: "relative", minHeight: 200 }}>
      <Stack gap={0}>
        <Group justify="space-between" align="center">
          <Text fw={600} size="lg">
            {searchParams.get("mode") == "preview"
              ? "Technical Analysis"
              : "Content Editor"}
          </Text>
        </Group>
        <Tabs
          value={tabValue}
          onChange={(tabName) => tabName && setSearchParams({ mode: tabName })}
          data-intercom-target="costory-tour-digest-content-tabs"
        >
          <Tabs.Panel value="edit">
            <Textarea
              ref={textareaRef}
              style={{ marginTop: 5, marginBottom: 5, border: "none" }}
              autosize
              autoFocus
              minRows={8}
              value={editContent}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setEditContent(e.target.value)
              }
              data-intercom-target="costory-tour-digest-content-textarea"
            />

            <div
              style={{ position: "absolute", top: 40, right: 10, zIndex: 2 }}
            >
              <ContributionAnalysisWand
                position={position}
                datePreset={DatePreset.LAST_MONTH}
                period={digestPeriod}
                setMarkdownContent={(content: string) =>
                  setEditContent(editContent + "\n" + content)
                }
                exclusionWhereClause={exclusionWhereClause}
                costColumn={costColumn}
                setSummaryValueFromBilly={setSummaryValueFromBilly}
              />
            </div>
          </Tabs.Panel>
          <Tabs.Panel value="preview">
            <Box style={{ position: "relative", minHeight: "60vh" }}>
              {loading && (
                <LoadingOverlay
                  visible
                  zIndex={1000}
                  loaderProps={{
                    children: (
                      <Group gap="sm" align="center">
                        <Loader size="sm" />
                        <Text size="sm" fw={500} c="textSecondary">
                          Adding chart...
                        </Text>
                      </Group>
                    ),
                  }}
                />
              )}
              {!loading && emptyContent && (
                <Flex pos="absolute" inset={0} justify="center" align="center">
                  {emptyPreview}
                </Flex>
              )}
              {!emptyContent && (
                <CustomMarkdown
                  imgWidth="80%"
                  maxWidth="100%"
                  content={editContent}
                />
              )}
              <div
                style={{ position: "absolute", top: 40, right: 10, zIndex: 2 }}
              >
                <ContributionAnalysisWand
                  position={position}
                  datePreset={DatePreset.LAST_MONTH}
                  period={digestPeriod}
                  setMarkdownContent={(content: string) =>
                    setEditContent(editContent + "\n" + content)
                  }
                  exclusionWhereClause={exclusionWhereClause}
                  costColumn={costColumn}
                  setSummaryValueFromBilly={setSummaryValueFromBilly}
                />
              </div>
            </Box>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Box>
  )
}
